import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  BarChart,
  Bar,
} from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartArea,
  faFlask,
  faSpinner,
  faSearch,
  faSync,
  faBook,
  faCircleQuestion,
} from '@fortawesome/free-solid-svg-icons';
import { indicatorsApi } from '../api/indicatorsApi';
import IndicatorDocsModal from '../components/IndicatorDocsModal';
import { getIndicatorDoc } from '../data/indicatorDocs';

function pct(n, digits = 1) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return `${(Number(n) * 100).toFixed(digits)}%`;
}

function num(n, digits = 2) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return Number(n).toFixed(digits);
}

function signalTone(signal) {
  const s = Number(signal);
  if (!Number.isFinite(s)) return 'text-gray-600';
  if (s > 0.05) return 'text-emerald-700';
  if (s < -0.05) return 'text-red-700';
  return 'text-amber-700';
}

function biasBadge(bias) {
  const b = String(bias || 'flat').toLowerCase();
  if (b === 'long') return 'bg-emerald-50 text-emerald-800 border-emerald-200';
  if (b === 'short') return 'bg-red-50 text-red-800 border-red-200';
  return 'bg-amber-50 text-amber-800 border-amber-200';
}

function Loading({ label = 'Computing…' }) {
  return (
    <div className="flex items-center justify-center py-16 text-gray-500 text-sm">
      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
      {label}
    </div>
  );
}

function EmptyHint({ text }) {
  return <div className="text-sm text-gray-500 py-10 text-center">{text}</div>;
}

function StatCard({ label, children, hint }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5">
      <div className="text-[10px] uppercase font-semibold text-gray-500">{label}</div>
      <div className="mt-0.5">{children}</div>
      {hint ? <div className="text-[10px] text-gray-500 mt-0.5">{hint}</div> : null}
    </div>
  );
}

function BiasPill({ bias }) {
  return (
    <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded border ${biasBadge(bias)}`}>
      {String(bias || 'flat').toUpperCase()}
    </span>
  );
}

function Interpretation({ text }) {
  if (!text) return null;
  return (
    <p className="text-[12px] text-gray-700 bg-indigo-50/60 border border-indigo-100 rounded-lg px-3 py-2 leading-relaxed">
      {text}
    </p>
  );
}

function MiniLine({ data, dataKey, name, color = '#4f46e5', domain, yFmt }) {
  if (!data?.length) return null;
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-2" style={{ height: 220 }}>
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="t"
            tick={{ fontSize: 9 }}
            tickFormatter={(v) => {
              try {
                return new Date(v).toLocaleDateString('en-US', {
                  timeZone: 'America/New_York',
                  month: 'short',
                  day: 'numeric',
                });
              } catch {
                return '';
              }
            }}
            minTickGap={28}
          />
          <YAxis domain={domain || ['auto', 'auto']} tick={{ fontSize: 10 }} width={42} tickFormatter={yFmt} />
          <Tooltip contentStyle={{ fontSize: 11 }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {domain && domain[0] < 0 && domain[1] > 0 ? (
            <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />
          ) : null}
          <Line
            type="monotone"
            dataKey={dataKey}
            name={name}
            stroke={color}
            strokeWidth={1.75}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function MarkovPanel({ result, loading }) {
  const path = result?.forwardPath || [];
  const chartData = useMemo(
    () =>
      path.map((p) => ({
        label: p.label,
        bull: Number((p.bull * 100).toFixed(2)),
        bear: Number((p.bear * 100).toFixed(2)),
        sideways: Number((p.sideways * 100).toFixed(2)),
        signal: Number(Number(p.signal).toFixed(3)),
      })),
    [path]
  );
  if (loading && !result) return <Loading label="Computing regime…" />;
  if (!result) return <EmptyHint text="Enter a symbol and run to see the forward regime path." />;

  const np = result.nextStateProbabilities || {};
  const matrix = result.transitionMatrix || [];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Current regime">
          <div className="text-lg font-bold text-gray-900">{result.currentRegime}</div>
        </StatCard>
        <StatCard label="Signal" hint="bull − bear (next bar)">
          <div className={`text-lg font-bold tabular-nums ${signalTone(result.signal)}`}>
            {num(result.signal, 3)}
          </div>
        </StatCard>
        <StatCard label="Bias">
          <BiasPill bias={result.bias} />
        </StatCard>
        <StatCard label="Bars used" hint={result.timeframe}>
          <div className="text-lg font-bold tabular-nums">{result.barsUsed}</div>
        </StatCard>
      </div>
      <div className="text-[11px] text-gray-600">
        Next · Bull <span className="font-semibold text-emerald-700">{pct(np.bull)}</span>
        {' · '}Bear <span className="font-semibold text-red-700">{pct(np.bear)}</span>
        {' · '}Side <span className="font-semibold">{pct(np.sideways)}</span>
      </div>

      <div>
        <h3 className="text-sm font-bold text-gray-800 mb-2">Forward path (next {path.length} bars)</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="rounded-lg border border-gray-200 bg-white p-2" style={{ height: 240 }}>
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis domain={[-1, 1]} tick={{ fontSize: 10 }} width={36} />
                <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />
                <Tooltip contentStyle={{ fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="signal" name="Signal" stroke="#4f46e5" strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-2" style={{ height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} width={36} />
                <Tooltip contentStyle={{ fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="bull" name="Bull %" stackId="a" fill="#059669" isAnimationActive={false} />
                <Bar dataKey="sideways" name="Side %" stackId="a" fill="#94a3b8" isAnimationActive={false} />
                <Bar dataKey="bear" name="Bear %" stackId="a" fill="#dc2626" isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="mt-3 max-h-48 overflow-auto rounded border border-gray-200">
          <table className="w-full text-[11px]">
            <thead className="bg-gray-50 sticky top-0">
              <tr className="text-left text-gray-500">
                <th className="px-2 py-1.5 font-semibold">Step</th>
                <th className="px-2 py-1.5 font-semibold">Most likely</th>
                <th className="px-2 py-1.5 font-semibold">Signal</th>
                <th className="px-2 py-1.5 font-semibold">Bull</th>
                <th className="px-2 py-1.5 font-semibold">Side</th>
                <th className="px-2 py-1.5 font-semibold">Bear</th>
                <th className="px-2 py-1.5 font-semibold">Bias</th>
              </tr>
            </thead>
            <tbody>
              {path.map((p) => (
                <tr key={p.step} className="border-t border-gray-100">
                  <td className="px-2 py-1.5 font-medium">{p.label}</td>
                  <td className="px-2 py-1.5">{p.mostLikely}</td>
                  <td className={`px-2 py-1.5 tabular-nums font-semibold ${signalTone(p.signal)}`}>{num(p.signal, 3)}</td>
                  <td className="px-2 py-1.5 tabular-nums text-emerald-700">{pct(p.bull)}</td>
                  <td className="px-2 py-1.5 tabular-nums">{pct(p.sideways)}</td>
                  <td className="px-2 py-1.5 tabular-nums text-red-700">{pct(p.bear)}</td>
                  <td className="px-2 py-1.5"><BiasPill bias={p.bias} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {matrix.length === 3 && (
        <div>
          <h3 className="text-sm font-bold text-gray-800 mb-2">Transition matrix</h3>
          <div className="overflow-auto rounded border border-gray-200 max-w-md">
            <table className="w-full text-[11px]">
              <thead className="bg-gray-50">
                <tr className="text-gray-500">
                  <th className="px-2 py-1.5 text-left font-semibold">From \\ To</th>
                  <th className="px-2 py-1.5 font-semibold">Bear</th>
                  <th className="px-2 py-1.5 font-semibold">Side</th>
                  <th className="px-2 py-1.5 font-semibold">Bull</th>
                </tr>
              </thead>
              <tbody>
                {['Bear', 'Sideways', 'Bull'].map((from, i) => (
                  <tr key={from} className="border-t border-gray-100">
                    <td className="px-2 py-1.5 font-medium">{from}</td>
                    {[0, 1, 2].map((j) => (
                      <td key={j} className="px-2 py-1.5 text-center tabular-nums">{pct(matrix[i][j])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <p className="text-[10px] text-gray-400">{result.disclaimer} {result.framework}</p>
    </div>
  );
}

function VolRegimePanel({ result, loading }) {
  if (loading && !result) return <Loading />;
  if (!result) return <EmptyHint text="Run to see realized vol regime." />;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Vol regime">
          <div className="text-lg font-bold capitalize">{result.currentRegime}</div>
        </StatCard>
        <StatCard label="Realized vol" hint={`${result.params?.window || 20}d ann.`}>
          <div className="text-lg font-bold tabular-nums">{num(result.realizedVolPct, 1)}%</div>
        </StatCard>
        <StatCard label="Percentile" hint="vs history">
          <div className="text-lg font-bold tabular-nums">{pct(result.percentile)}</div>
        </StatCard>
        <StatCard label="Bias">
          <div className={`text-lg font-bold tabular-nums ${signalTone(result.signal)}`}>{num(result.signal, 2)}</div>
          <BiasPill bias={result.bias} />
        </StatCard>
      </div>
      <Interpretation text={result.interpretation} />
      <MiniLine data={result.series} dataKey="vol" name="Realized vol %" color="#ea580c" yFmt={(v) => `${v}%`} />
    </div>
  );
}

function TsMomentumPanel({ result, loading }) {
  if (loading && !result) return <Loading />;
  if (!result) return <EmptyHint text="Run to see multi-horizon momentum." />;
  const legs = result.legs || {};
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {['1m', '3m', '6m', '12m'].map((k) => (
          <StatCard key={k} label={k.toUpperCase()}>
            <div className={`text-lg font-bold tabular-nums ${signalTone(legs[k])}`}>{pct(legs[k])}</div>
          </StatCard>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatCard label="Avg return">
          <div className={`text-lg font-bold tabular-nums ${signalTone(result.averageReturn)}`}>
            {pct(result.averageReturn)}
          </div>
        </StatCard>
        <StatCard label="Signal">
          <div className={`text-lg font-bold tabular-nums ${signalTone(result.signal)}`}>{num(result.signal, 3)}</div>
        </StatCard>
        <StatCard label="Bias">
          <BiasPill bias={result.bias} />
        </StatCard>
      </div>
      <Interpretation text={result.interpretation} />
      <MiniLine data={result.series} dataKey="mom12" name="12m momentum %" color="#2563eb" domain={['auto', 'auto']} yFmt={(v) => `${v}%`} />
    </div>
  );
}

function MeanRevPanel({ result, loading }) {
  if (loading && !result) return <Loading />;
  if (!result) return <EmptyHint text="Run to see SMA / VWAP z-scores." />;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Z vs SMA" hint={`SMA ${result.smaPeriod}`}>
          <div className={`text-lg font-bold tabular-nums ${signalTone(-result.zSma)}`}>{num(result.zSma, 2)}σ</div>
        </StatCard>
        <StatCard label="Z vs VWAP">
          <div className="text-lg font-bold tabular-nums">{result.zVwap == null ? '—' : `${num(result.zVwap, 2)}σ`}</div>
        </StatCard>
        <StatCard label="Setup">
          <div className="text-sm font-bold text-gray-900">{result.setup}</div>
        </StatCard>
        <StatCard label="Bias">
          <BiasPill bias={result.bias} />
        </StatCard>
      </div>
      <Interpretation text={result.interpretation} />
      <MiniLine data={result.series} dataKey="z" name="Z-score" color="#7c3aed" domain={[-3, 3]} />
    </div>
  );
}

function BetaCorrPanel({ result, loading }) {
  if (loading && !result) return <Loading />;
  if (!result) return <EmptyHint text="Run to see beta & correlation vs benchmark." />;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Beta" hint={`vs ${result.benchmark || 'SPY'} · ${result.window}d`}>
          <div className="text-lg font-bold tabular-nums">{num(result.beta, 2)}</div>
        </StatCard>
        <StatCard label="Correlation">
          <div className="text-lg font-bold tabular-nums">{num(result.correlation, 2)}</div>
        </StatCard>
        <StatCard label="Risk label">
          <div className="text-sm font-bold text-gray-900">{result.riskLabel}</div>
        </StatCard>
        <StatCard label="Bias">
          <BiasPill bias={result.bias} />
        </StatCard>
      </div>
      <Interpretation text={result.interpretation} />
    </div>
  );
}

function SmaStackPanel({ result, loading }) {
  if (loading && !result) return <Loading />;
  if (!result) return <EmptyHint text="Run to see SMA trend stack." />;
  const series = (result.series || []).map((r) => ({
    t: r.t,
    close: r.close,
    smaFast: r.smaFast,
    smaSlow: r.smaSlow,
  }));
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Price">
          <div className="text-lg font-bold tabular-nums">${num(result.price)}</div>
        </StatCard>
        <StatCard label={`SMA ${result.fast}`}>
          <div className="text-lg font-bold tabular-nums">${num(result.smaFast)}</div>
        </StatCard>
        <StatCard label={`SMA ${result.slow}`}>
          <div className="text-lg font-bold tabular-nums">${num(result.smaSlow)}</div>
        </StatCard>
        <StatCard label="Bias">
          <BiasPill bias={result.bias} />
          {(result.goldenCross || result.deathCross) && (
            <div className="text-[10px] font-semibold text-indigo-700 mt-1">
              {result.goldenCross ? 'Golden cross' : 'Death cross'}
            </div>
          )}
        </StatCard>
      </div>
      <Interpretation text={result.interpretation} />
      {series.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white p-2" style={{ height: 240 }}>
          <ResponsiveContainer>
            <LineChart data={series} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="t" tick={{ fontSize: 9 }} tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/New_York' })} minTickGap={28} />
              <YAxis domain={['auto', 'auto']} tick={{ fontSize: 10 }} width={48} />
              <Tooltip contentStyle={{ fontSize: 11 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="close" name="Close" stroke="#111827" dot={false} strokeWidth={1.5} isAnimationActive={false} />
              <Line type="monotone" dataKey="smaFast" name={`SMA ${result.fast}`} stroke="#2563eb" dot={false} strokeWidth={1.25} isAnimationActive={false} />
              <Line type="monotone" dataKey="smaSlow" name={`SMA ${result.slow}`} stroke="#f59e0b" dot={false} strokeWidth={1.25} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function AtrStretchPanel({ result, loading }) {
  if (loading && !result) return <Loading />;
  if (!result) return <EmptyHint text="Run to see ATR stretch." />;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Stretch" hint="ATR units">
          <div className={`text-lg font-bold tabular-nums ${signalTone(-result.stretchAtr)}`}>
            {num(result.stretchAtr, 2)}
          </div>
        </StatCard>
        <StatCard label="ATR">
          <div className="text-lg font-bold tabular-nums">${num(result.atr)}</div>
        </StatCard>
        <StatCard label="Setup">
          <div className="text-sm font-bold">{result.setup}</div>
        </StatCard>
        <StatCard label="Bias">
          <BiasPill bias={result.bias} />
        </StatCard>
      </div>
      <Interpretation text={result.interpretation} />
      <MiniLine data={result.series} dataKey="stretch" name="ATR stretch" color="#dc2626" domain={[-3, 3]} />
    </div>
  );
}

const INDICATOR_PANELS = {
  markov: MarkovPanel,
  vol_regime: VolRegimePanel,
  ts_momentum: TsMomentumPanel,
  mean_reversion_z: MeanRevPanel,
  beta_corr: BetaCorrPanel,
  sma_stack: SmaStackPanel,
  atr_stretch: AtrStretchPanel,
};

/** Per-indicator param fields shown in the toolbar */
function ParamFields({ selectedId, params, setParams }) {
  const set = (key, value) => setParams((p) => ({ ...p, [key]: value }));

  if (selectedId === 'markov') {
    return (
      <>
        <NumField label="Horizon" value={params.horizon} onChange={(v) => set('horizon', v)} min={1} max={30} />
        <NumField label="Window" value={params.window} onChange={(v) => set('window', v)} min={5} max={120} />
        <NumField label="Threshold" value={params.threshold} onChange={(v) => set('threshold', v)} step={0.01} min={0.01} max={0.5} />
      </>
    );
  }
  if (selectedId === 'vol_regime') {
    return (
      <>
        <NumField label="Vol window" value={params.window} onChange={(v) => set('window', v)} min={5} max={60} />
        <NumField label="Lookback" value={params.lookback} onChange={(v) => set('lookback', v)} min={60} max={500} />
      </>
    );
  }
  if (selectedId === 'ts_momentum') {
    return (
      <label className="text-[10px] font-semibold uppercase text-gray-500 flex items-center gap-2 h-9 mt-5">
        <input
          type="checkbox"
          checked={params.skipLastMonth !== false}
          onChange={(e) => set('skipLastMonth', e.target.checked)}
        />
        Skip last month
      </label>
    );
  }
  if (selectedId === 'mean_reversion_z') {
    return (
      <>
        <NumField label="SMA period" value={params.smaPeriod} onChange={(v) => set('smaPeriod', v)} min={5} max={100} />
        <NumField label="Entry |z|" value={params.entryZ} onChange={(v) => set('entryZ', v)} step={0.1} min={1} max={4} />
      </>
    );
  }
  if (selectedId === 'beta_corr') {
    return (
      <>
        <label className="text-[10px] font-semibold uppercase text-gray-500">
          Benchmark
          <input
            value={params.benchmark || 'SPY'}
            onChange={(e) => set('benchmark', e.target.value.toUpperCase())}
            className="mt-1 block h-9 w-24 px-2 rounded-lg border border-gray-200 text-sm font-semibold"
          />
        </label>
        <NumField label="Window" value={params.window} onChange={(v) => set('window', v)} min={20} max={252} />
      </>
    );
  }
  if (selectedId === 'sma_stack') {
    return (
      <>
        <NumField label="Fast SMA" value={params.fast} onChange={(v) => set('fast', v)} min={10} max={100} />
        <NumField label="Slow SMA" value={params.slow} onChange={(v) => set('slow', v)} min={50} max={300} />
      </>
    );
  }
  if (selectedId === 'atr_stretch') {
    return (
      <>
        <NumField label="ATR len" value={params.atrLen} onChange={(v) => set('atrLen', v)} min={5} max={40} />
        <NumField label="Mean len" value={params.meanLen} onChange={(v) => set('meanLen', v)} min={5} max={60} />
        <NumField label="Entry ATR" value={params.entryAtr} onChange={(v) => set('entryAtr', v)} step={0.1} min={1} max={5} />
      </>
    );
  }
  return null;
}

function NumField({ label, value, onChange, min, max, step = 1 }) {
  return (
    <label className="text-[10px] font-semibold uppercase text-gray-500">
      {label}
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-1 block h-9 w-24 px-2 rounded-lg border border-gray-200 text-sm"
      />
    </label>
  );
}

const DEFAULT_PARAMS = {
  horizon: 5,
  window: 20,
  threshold: 0.05,
  lookback: 252,
  skipLastMonth: true,
  smaPeriod: 20,
  entryZ: 2,
  benchmark: 'SPY',
  fast: 50,
  slow: 200,
  atrLen: 14,
  meanLen: 20,
  entryAtr: 2,
};

const IndicatorsPage = () => {
  const [catalog, setCatalog] = useState([]);
  const [catalogError, setCatalogError] = useState('');
  const [selectedId, setSelectedId] = useState('markov');
  const [symbol, setSymbol] = useState('SPY');
  const [params, setParams] = useState({ ...DEFAULT_PARAMS });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [docsOpen, setDocsOpen] = useState(false);
  const [docsFocusId, setDocsFocusId] = useState('markov');

  const openDocs = useCallback((id) => {
    setDocsFocusId(id || selectedId || 'markov');
    setDocsOpen(true);
  }, [selectedId]);

  const selected = useMemo(
    () => catalog.find((c) => c.id === selectedId) || null,
    [catalog, selectedId]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await indicatorsApi.list();
        if (cancelled) return;
        if (!res.success) throw new Error(res.error || 'Failed to load indicators');
        setCatalog(res.data || []);
        if (res.data?.length && !res.data.some((d) => d.id === selectedId)) {
          setSelectedId(res.data[0].id);
        }
      } catch (err) {
        if (!cancelled) setCatalogError(err.message || 'Failed to load catalog');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const run = useCallback(async () => {
    const sym = String(symbol || '').trim().toUpperCase();
    if (!sym || !selectedId) return;
    setBusy(true);
    setError('');
    try {
      const res = await indicatorsApi.run(selectedId, { symbol: sym, ...params });
      if (!res.success) throw new Error(res.error || 'Run failed');
      setResult(res.data);
    } catch (err) {
      setResult(null);
      setError(err.response?.data?.error || err.message || 'Run failed');
    } finally {
      setBusy(false);
    }
  }, [symbol, selectedId, params]);

  useEffect(() => {
    setResult(null);
    setError('');
    // Seed params from catalog defaults when switching
    const def = catalog.find((c) => c.id === selectedId)?.defaultParams;
    if (def) setParams((p) => ({ ...p, ...def }));
  }, [selectedId, catalog]);

  const Panel = INDICATOR_PANELS[selectedId] || null;

  return (
    <main className="flex-1 container mx-auto px-4 lg:px-6 py-6">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2 flex items-center gap-2">
            <FontAwesomeIcon icon={faChartArea} className="text-indigo-600 text-2xl" />
            Indicators
          </h1>
          <p className="text-gray-600 max-w-3xl">
            Research desk for quant-style indicators. Pick a model, plug in a symbol, inspect bias and
            charts. New indicators register on the server and appear in the library automatically.
          </p>
        </div>
        <button
          type="button"
          onClick={() => openDocs(selectedId)}
          className="h-9 px-3.5 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-800 text-sm font-semibold hover:bg-indigo-100 inline-flex items-center gap-2 shrink-0"
        >
          <FontAwesomeIcon icon={faBook} className="text-xs" />
          Help / Docs
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <aside className="lg:col-span-3 space-y-2">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 px-1">
            Library ({catalog.length})
          </div>
          {catalogError && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded px-2 py-1.5">{catalogError}</p>
          )}
          {catalog.map((item) => {
            const active = item.id === selectedId;
            const hasDoc = Boolean(getIndicatorDoc(item.id));
            return (
              <div
                key={item.id}
                className={`relative rounded-lg border transition-colors ${
                  active ? 'border-indigo-300 bg-indigo-50 shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <button
                  type="button"
                  onClick={() => setSelectedId(item.id)}
                  className="w-full text-left px-3 py-2.5 pr-9"
                >
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faFlask} className={`text-xs ${active ? 'text-indigo-600' : 'text-gray-400'}`} />
                    <span className={`text-sm font-semibold ${active ? 'text-indigo-900' : 'text-gray-800'}`}>{item.name}</span>
                  </div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-400 mt-1">{item.category}</div>
                  <p className="text-[11px] text-gray-500 mt-1 leading-snug line-clamp-3">{item.description}</p>
                </button>
                {hasDoc ? (
                  <button
                    type="button"
                    title={`Docs: ${item.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      openDocs(item.id);
                    }}
                    className="absolute top-2 right-2 h-7 w-7 rounded-md text-gray-400 hover:text-indigo-700 hover:bg-indigo-100/80 inline-flex items-center justify-center"
                    aria-label={`Open docs for ${item.name}`}
                  >
                    <FontAwesomeIcon icon={faCircleQuestion} className="text-sm" />
                  </button>
                ) : null}
              </div>
            );
          })}
        </aside>

        <section className="lg:col-span-9 space-y-4">
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm space-y-3">
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-[10px] font-semibold uppercase text-gray-500">
                Symbol
                <div className="mt-1 flex items-center gap-1.5 h-9 px-2 rounded-lg border border-gray-200 bg-gray-50">
                  <FontAwesomeIcon icon={faSearch} className="text-[10px] text-gray-400" />
                  <input
                    value={symbol}
                    onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === 'Enter' && run()}
                    className="bg-transparent text-sm font-semibold text-gray-900 w-28 focus:outline-none"
                    placeholder="SPY"
                  />
                </div>
              </label>
              <ParamFields selectedId={selectedId} params={params} setParams={setParams} />
              <button
                type="button"
                onClick={run}
                disabled={busy || !symbol.trim()}
                className="h-9 px-4 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                <FontAwesomeIcon icon={busy ? faSpinner : faSync} className={busy ? 'animate-spin' : ''} />
                {busy ? 'Running…' : 'Run'}
              </button>
              <button
                type="button"
                onClick={() => openDocs(selectedId)}
                className="h-9 px-3 rounded-lg border border-gray-200 bg-white text-gray-700 text-sm font-semibold hover:bg-gray-50 inline-flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faCircleQuestion} className="text-indigo-600" />
                Docs
              </button>
            </div>
            {selected && (
              <div className="text-[11px] text-gray-500 leading-relaxed border-t border-gray-100 pt-2 flex flex-wrap items-start justify-between gap-2">
                <p className="flex-1 min-w-0">{selected.description}</p>
                {getIndicatorDoc(selectedId) ? (
                  <button
                    type="button"
                    onClick={() => openDocs(selectedId)}
                    className="text-indigo-700 font-semibold hover:underline shrink-0"
                  >
                    Read full guide →
                  </button>
                ) : null}
              </div>
            )}
          </div>

          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>
          )}

          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm min-h-[320px]">
            {Panel ? (
              <Panel result={result} loading={busy} />
            ) : (
              <EmptyHint text={`No UI panel for ${selectedId} yet — backend may still run it.`} />
            )}
          </div>
        </section>
      </div>

      <IndicatorDocsModal
        open={docsOpen}
        onClose={() => setDocsOpen(false)}
        initialId={docsFocusId}
      />
    </main>
  );
};

export default IndicatorsPage;
